import {
  JobProgressionFlow,
  JobProgressionStepStatus,
} from '../../../../../jobs/progression/JobProgression'
import {
  ExtensionBillingInfoType,
  InventoryItemExtensionBillingType,
} from '../../../InventoryItem'
import { RegularEditorViewModel } from './BillingEditorViewModel'

export enum AllowedProgressionTypes {
  OUTSOURCING = JobProgressionFlow.OUTSOURCING,
  RETAIL = JobProgressionFlow.RETAIL,
  ESSENTIAL = JobProgressionFlow.ESSENTIAL,
  PREPARATION = JobProgressionFlow.PREPARATION,
  REVIEW = JobProgressionFlow.REVIEW,
}

export enum AllowedProgressionSteps {
  JOB_STARTED = JobProgressionStepStatus.JOB_STARTED,
  OPEN_ITEMS = JobProgressionStepStatus.OPEN_ITEMS,
  DRAFT_IN_REVIEW = JobProgressionStepStatus.DRAFT_IN_REVIEW,
  DRAFT_REJECTED = JobProgressionStepStatus.DRAFT_REJECTED,
  DRAFT_APPROVED = JobProgressionStepStatus.DRAFT_APPROVED,
  AUTHORIZATION_SIGNED = JobProgressionStepStatus.AUTHORIZATION_SIGNED,
  RETURN_FILED = JobProgressionStepStatus.RETURN_FILED,
  DELIVERABLE_COMPLETED = JobProgressionStepStatus.DELIVERABLE_COMPLETED,
  PREPARATION_COMPLETED = JobProgressionStepStatus.PREPARATION_COMPLETED,
  REVIEW_COMPLETED = JobProgressionStepStatus.REVIEW_COMPLETED,
}

export const ProgressionsNameMapping: {
  [key in AllowedProgressionTypes]: string
} = {
  [AllowedProgressionTypes.OUTSOURCING]: 'Outsourcing',
  [AllowedProgressionTypes.ESSENTIAL]: 'Essential',
  [AllowedProgressionTypes.REVIEW]: 'Review',
  [AllowedProgressionTypes.RETAIL]: 'Retail',
  [AllowedProgressionTypes.PREPARATION]: 'Preparation',
}

export const ProgressionStepsNameMapping: {
  [key in AllowedProgressionSteps]: string
} = {
  [AllowedProgressionSteps.JOB_STARTED]: 'Job Started',
  [AllowedProgressionSteps.OPEN_ITEMS]: 'Open Items',
  [AllowedProgressionSteps.DRAFT_IN_REVIEW]: 'Draft in Review',
  [AllowedProgressionSteps.DRAFT_APPROVED]: 'Draft Approved',
  [AllowedProgressionSteps.DRAFT_REJECTED]: 'Draft Rejected',
  [AllowedProgressionSteps.REVIEW_COMPLETED]: 'Review Completed',
  [AllowedProgressionSteps.RETURN_FILED]: 'Return Filed',
  [AllowedProgressionSteps.AUTHORIZATION_SIGNED]: 'Authorization Signed',
  [AllowedProgressionSteps.DELIVERABLE_COMPLETED]: 'Deliverable Completed',
  [AllowedProgressionSteps.PREPARATION_COMPLETED]: 'Preparation Completed',
}

export const AvailableProgressions: AvailableProgressionOption[] = [
  {
    name: ProgressionsNameMapping[AllowedProgressionTypes.OUTSOURCING],
    value: AllowedProgressionTypes.OUTSOURCING,
  },
  {
    name: ProgressionsNameMapping[AllowedProgressionTypes.ESSENTIAL],
    value: AllowedProgressionTypes.ESSENTIAL,
  },
  {
    name: ProgressionsNameMapping[AllowedProgressionTypes.REVIEW],
    value: AllowedProgressionTypes.REVIEW,
  },
  {
    name: ProgressionsNameMapping[AllowedProgressionTypes.RETAIL],
    value: AllowedProgressionTypes.RETAIL,
  },
  {
    name: ProgressionsNameMapping[AllowedProgressionTypes.PREPARATION],
    value: AllowedProgressionTypes.PREPARATION,
  },
]

export const AvailableProgressionSteps: AvailableProgressionStepsMapping = {
  [AllowedProgressionTypes.OUTSOURCING]: [
    {
      name: 'Job Started',
      value: AllowedProgressionSteps.JOB_STARTED,
    },
    {
      name: 'Open Items',
      value: AllowedProgressionSteps.OPEN_ITEMS,
    },
    {
      name: 'Draft Approved',
      value: AllowedProgressionSteps.DRAFT_APPROVED,
    },
    {
      name: 'Draft in Review',
      value: AllowedProgressionSteps.DRAFT_IN_REVIEW,
    },
    {
      name: 'Draft Rejected',
      value: AllowedProgressionSteps.DRAFT_REJECTED,
    },
  ],
  [AllowedProgressionTypes.ESSENTIAL]: [
    {
      name: 'Job Started',
      value: AllowedProgressionSteps.JOB_STARTED,
    },
    {
      name: 'Deliverable Completed',
      value: AllowedProgressionSteps.DELIVERABLE_COMPLETED,
    },
  ],
  [AllowedProgressionTypes.PREPARATION]: [
    {
      name: 'Job Started',
      value: AllowedProgressionSteps.JOB_STARTED,
    },
    {
      name: 'Open Items',
      value: AllowedProgressionSteps.OPEN_ITEMS,
    },
    {
      name: 'Preparation Completed',
      value: AllowedProgressionSteps.PREPARATION_COMPLETED,
    },
  ],
  [AllowedProgressionTypes.RETAIL]: [
    {
      name: 'Job Started',
      value: AllowedProgressionSteps.JOB_STARTED,
    },
    {
      name: 'Open Items',
      value: AllowedProgressionSteps.OPEN_ITEMS,
    },
    {
      name: 'Draft Approved',
      value: AllowedProgressionSteps.DRAFT_APPROVED,
    },
    {
      name: 'Draft in Review',
      value: AllowedProgressionSteps.DRAFT_IN_REVIEW,
    },
    {
      name: 'Draft Rejected',
      value: AllowedProgressionSteps.DRAFT_REJECTED,
    },
    {
      name: 'Authorization Signed',
      value: AllowedProgressionSteps.AUTHORIZATION_SIGNED,
    },
    {
      name: 'Return Filed',
      value: AllowedProgressionSteps.RETURN_FILED,
    },
  ],
  [AllowedProgressionTypes.REVIEW]: [
    {
      name: 'Job Started',
      value: AllowedProgressionSteps.JOB_STARTED,
    },
    {
      name: 'Open Items',
      value: AllowedProgressionSteps.OPEN_ITEMS,
    },
    {
      name: 'Draft Approved',
      value: AllowedProgressionSteps.DRAFT_APPROVED,
    },
    {
      name: 'Draft in Review',
      value: AllowedProgressionSteps.DRAFT_IN_REVIEW,
    },
    {
      name: 'Draft Rejected',
      value: AllowedProgressionSteps.DRAFT_REJECTED,
    },
    {
      name: 'Review Completed',
      value: AllowedProgressionSteps.REVIEW_COMPLETED,
    },
  ],
}

export type AvailableProgressionStepsMapping = {
  [key in AllowedProgressionTypes]: AvailableProgressionStep[]
}

export type getBillingType = () => InventoryItemExtensionBillingType

export type AvailableProgressionOption = {
  name: string
  value: AllowedProgressionTypes
}

export interface AvailableProgressionStep {
  name: string
  value: AllowedProgressionSteps
}

export interface SelectedProgression extends AvailableProgressionOption {
  steps: SelectedProgressionStep[]
}

export interface SelectedProgressionStep extends AvailableProgressionStep {
  pricingVM: RegularEditorViewModel
}

export type progressionMap = Map<AllowedProgressionTypes, SelectedProgression>

export type StepMap = Map<AllowedProgressionSteps, SelectedProgressionStep>

export type ProgressionPricing = {
  progression: AllowedProgressionTypes
  steps: {
    step: AllowedProgressionSteps
    clientChargeAmount: number
    proPayoutAmount: number
  }[]
}

export type ExtensionProgressionBilling = {
  type: ExtensionBillingInfoType.Progression
  progressions: ProgressionPricing[]
}

export type ExtensionRegularBilling = {
  type: ExtensionBillingInfoType.Regular
  clientChargeAmount: number
  proPayoutAmount: number
}

export type ExtensionBillingInfo =
  | ExtensionRegularBilling
  | ExtensionProgressionBilling
